import { STORES, getItem, putItem, deleteItem, clearStore, getAllItems } from './indexedDB';

const moveWithinArray = (arr, from, to) => {
  if (from === to) {
    return arr;
  }
  const item = arr.splice(from, 1)[0];
  arr.splice(to, 0, item);
  return arr;
};

const moveAfterItem = (arr, from, after) => {
  // Why would you move an item after itself? It'll be in the same place.
  if (from === after) {
    return arr;
  }
  // To put an item after another, it should be the next index.
  // But if the item is before the one it's being moved after,
  // it needs to compensate for its own spot disappearing by being the same index.
  if (from > after) {
    ++after;
  }
  return moveWithinArray(arr, from, after);
};

const moveAfterItemByItem = (arr, item, afterItem) => {
  const fromIndex = arr.indexOf(item);
  const afterIndex = arr.indexOf(afterItem);
  if (fromIndex === -1 || afterIndex === -1) {
    return arr;
  }
  return moveAfterItem(arr, fromIndex, afterIndex);
};

const isEmptyObject = (obj) => {
  for (let i in obj) {
    return false;
  }
  return true;
};

const findInObj = (obj, conditionFunction) => {
  // TO DO: Generalise function to take any array methods, e.g. some, filter

  // Iterating over the object values allows us to pass in a function that uses the same syntax as Array.find()
  const objPropsArray = Object.values(obj);
  return objPropsArray.find((prop) => {
    return conditionFunction(prop);
  });
};

/**
 * Converts a value to a number, returning a fallback if parsing fails
 * @param {number|string} value - The value to convert to a number
 * @param {number} fallback - Value to return if parsing fails
 * @returns {number} The parsed number or fallback value
 */
const asNumber = (value, fallback = 0) => {
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed)
    ? fallback
    : parsed;
};

/**
 * Formats a string template by replacing {n} placeholders with provided parameters
 * @param {string} template - The string template containing {n} placeholders
 * @param {...*} params - Values to insert into the template
 * @returns {string} The formatted string with placeholders replaced by parameters
 */
const formatString = (template, ...params) => {
  const length = params.length;

  return template.replace(/{(\d+)}/g, (match, index) =>
    index >= length
      ? match
      : `${params[index]}`,
  );
};

/**
 * Checks if a value is defined (not undefined)
 * @param {*} value - The value to check
 * @returns {boolean} True if the value is defined, false otherwise
 */
const isDefined = (value) => {
  return value !== undefined;
};

const isElement = (node) => {
  return node instanceof Node && node.nodeType === Node.ELEMENT_NODE;
};

const handleize = (value) => {
  return value
    .toLowerCase()
    .replace(/ /g, '-');
};

const removeParams = (keys) => {
  const url = new URL(location.href);
  keys.forEach((key) => url.searchParams.delete(key));
  history.replaceState(null, '', url);
};

const convertObjectToFormData = (object) => {
  const formData = new FormData();
  Object.entries(object).forEach(([key, value]) => {
    formData.append(key, value);
  });
  return formData;
};

const convertFormDataToObject = (formData) => {
  const object = {};
  formData.forEach((value, key) => {
    if (['true', 'false'].includes(value)) {
      object[key] = value === 'true';
      return;
    }
    object[key] = value;
  });
  return object;
};

const wait = (ms) => new Promise((resolve, reject) => setTimeout(resolve, ms));

const safeSessionStorage = (operation, key, value) => {
  try {
    if (operation === 'get') {
      return sessionStorage.getItem(key);
    } else if (operation === 'set') {
      sessionStorage.setItem(key, value);
      return true;
    }
  } catch (e) {
    console.warn('SessionStorage not available:', e);
    return operation === 'get'
      ? null
      : false;
  }
};

/**
 * Sets a nested value in an object using a dot-separated path
 * @param {Object} obj - The target object
 * @param {string} path - The dot-separated path (e.g., 'variants.price')
 * @param {*} value - The value to set
 */
const setNestedValue = (obj, path, value) => {
  const keys = path.split('.');
  let current = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!(key in current) || typeof current[key] !== 'object' || current[key] === null) {
      current[key] = {};
    }
    current = current[key];
  }

  current[keys[keys.length - 1]] = value;
};

/**
 * Creates a smart product caching utility with static/dynamic field separation
 * Static fields (images, descriptions, etc.) cached for 48 hours
 * Dynamic fields (pricing, availability, etc.) cached for 5 minutes
 *
 * @returns {Object} An object containing methods to interact with the smart product cache
 *
 * @property {Function} get - Retrieves a cached product by its id with cache status
 * @property {Function} set - Stores a product in the cache with appropriate timestamps
 * @property {Function} isValid - Checks if cached data is valid for specific field types
 * @property {Function} needsUpdate - Determines what type of cache update is needed
 * @property {Function} clear - Removes all cached products
 * @property {Function} removeExpired - Removes expired items based on field-specific expiry
 *
 * @example
 * const cache = productCache();
 *
 * // Store a product
 * await cache.set('my-product', productData);
 *
 * // Retrieve a product with cache status
 * const cached = await cache.get('my-product');
 * const updateNeeds = cache.needsUpdate(cached);
 *
 * if (updateNeeds.none) {
 *   // Use cached product
 * } else if (updateNeeds.dynamicOnly) {
 *   // Fetch only dynamic data
 * }
 */
const smartProductCache = () => {
  // Dynamic fields that change frequently - everything else is static
  const DYNAMIC_FIELDS = new Set([
    'available',
    'tags',
    'compare_at_price',
    'compare_at_price_min',
    'compare_at_price_max',
    'price',
    'price_min',
    'price_max',
    'variants.price',
    'variants.compare_at_price',
    'variants.available',
  ]);

  const CACHE_DURATIONS = {
    PRODUCT: 48 * 60 * 60 * 1000, // 48 hours for full product data
    DYNAMIC: 5 * 60 * 1000, // 5 minutes for dynamic data
  };

  const isDynamicField = (fieldPath) => {
    return DYNAMIC_FIELDS.has(fieldPath) || DYNAMIC_FIELDS.has(fieldPath.replace(/\[\d+\]/, ''));
  };

  // Extract only dynamic fields from product data
  const extractDynamicData = (productData) => {
    const dynamicData = {};

    const processObject = (obj, currentPath = '', target) => {
      for (const [key, value] of Object.entries(obj)) {
        const fullPath = currentPath
          ? `${currentPath}.${key}`
          : key;

        if (isDynamicField(fullPath)) {
          setNestedValue(target, fullPath, value);
        } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          processObject(value, fullPath, target);
        } else if (Array.isArray(value)) {
          // Handle arrays (like variants)
          if (fullPath === 'variants') {
            target.variants = value
              .map((variant) => {
                const dynamicVariant = {};
                if (variant.price !== undefined) dynamicVariant.price = variant.price;
                if (variant.compare_at_price !== undefined) dynamicVariant.compare_at_price = variant.compare_at_price;
                if (variant.available !== undefined) dynamicVariant.available = variant.available;
                return dynamicVariant;
              })
              .filter((v) => Object.keys(v).length > 0);
          }
        }
      }
    };

    // Handle { product, siblings } structure
    if (productData.product && productData.siblings) {
      // Pure {product, siblings} structure
      if (productData.product) {
        dynamicData.product = {};
        processObject(productData.product, '', dynamicData.product);
      }

      if (productData.siblings && Array.isArray(productData.siblings)) {
        dynamicData.siblings = productData.siblings
          .map((sibling) => {
            const dynamicSibling = {};
            processObject(sibling, '', dynamicSibling);
            return dynamicSibling;
          })
          .filter((s) => Object.keys(s).length > 0);
      }
    } else if (productData.siblings && !productData.product) {
      // Mixed case: flat product data with siblings property (like from convertGraphQLDynamicToLiquidProduct)
      // Extract product fields from the main object, excluding siblings
      const productCopy = { ...productData };
      delete productCopy.siblings;

      dynamicData.product = {};
      processObject(productCopy, '', dynamicData.product);

      // Process siblings separately
      if (Array.isArray(productData.siblings)) {
        dynamicData.siblings = productData.siblings
          .map((sibling) => {
            const dynamicSibling = {};
            processObject(sibling, '', dynamicSibling);
            return dynamicSibling;
          })
          .filter((s) => Object.keys(s).length > 0);
      }
    } else {
      // Handle flat product data - wrap in product structure for consistency
      dynamicData.product = {};
      processObject(productData, '', dynamicData.product);
    }

    return dynamicData;
  };

  // Merge dynamic data into product data
  const mergeDynamicData = (productData, dynamicData) => {
    if (!dynamicData || !productData) return productData;

    const merged = JSON.parse(JSON.stringify(productData)); // Deep clone

    const mergeObject = (target, source) => {
      for (const [key, value] of Object.entries(source)) {
        if (key === 'variants' && Array.isArray(target.variants) && Array.isArray(value)) {
          // Merge variants by ID
          const dynamicVariantsMap = new Map();
          value.forEach((variant) => {
            if (variant.id) dynamicVariantsMap.set(variant.id, variant);
          });

          target.variants = target.variants.map((staticVariant) => {
            const dynamicVariant = dynamicVariantsMap.get(staticVariant.id);
            return dynamicVariant
              ? { ...staticVariant, ...dynamicVariant }
              : staticVariant;
          });
        } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          if (!target[key]) target[key] = {};
          mergeObject(target[key], value);
        } else {
          target[key] = value;
        }
      }
    };

    // Handle { product, siblings } structure
    if (dynamicData.product && merged.product) {
      mergeObject(merged.product, dynamicData.product);
    }

    if (dynamicData.siblings && merged.siblings) {
      const dynamicSiblingsMap = new Map();
      dynamicData.siblings.forEach((sibling) => {
        if (sibling.id) dynamicSiblingsMap.set(sibling.id, sibling);
      });

      merged.siblings = merged.siblings.map((staticSibling) => {
        const dynamicSibling = dynamicSiblingsMap.get(staticSibling.id);
        return dynamicSibling
          ? { ...staticSibling, ...dynamicSibling }
          : staticSibling;
      });
    }

    return merged;
  };

  const setNestedValue = (obj, path, value) => {
    const keys = path.split('.');
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!(key in current) || typeof current[key] !== 'object') {
        current[key] = {};
      }
      current = current[key];
    }

    current[keys[keys.length - 1]] = value;
  };

  return {
    /**
     * Retrieves a cached product by its id with cache status
     * @param {string} id - The product id to look up
     * @returns {Promise<Object|null>} The cached item with _cacheStatus if found
     */
    get: async (id) => {
      try {
        const cached = await getItem(STORES.PRODUCTS_CACHE, id);
        if (!cached) return null;

        const now = Date.now();

        const productValid = cached.productTimestamp && now - cached.productTimestamp < CACHE_DURATIONS.PRODUCT;
        const dynamicValid = cached.dynamicTimestamp && now - cached.dynamicTimestamp < CACHE_DURATIONS.DYNAMIC;

        // Handle backward compatibility - if no dynamicData, treat as legacy cache
        if (!cached.dynamicData) {
          // Handle both old format and new format for backward compatibility
          const legacyProduct = cached.product?.product || cached.product || null;
          const legacySiblings = cached.product?.siblings || [];

          return {
            product: legacyProduct,
            siblings: legacySiblings,
            id: cached.id,
            productTimestamp: cached.timestamp || now,
            dynamicTimestamp: cached.timestamp || now,
            timestamp: cached.timestamp || now,
            dynamicData: null,
            _cacheStatus: {
              product: productValid,
              dynamic: dynamicValid,
              needsRefresh: !productValid || !dynamicValid,
            },
          };
        }

        // Merge dynamic data into product data if available and valid
        let finalProduct = cached.product;
        if (dynamicValid && cached.dynamicData) {
          finalProduct = mergeDynamicData(cached.product, cached.dynamicData);
        }

        // Ensure we always return a valid product structure, even if data is missing
        // Handle both nested structure {product: {...}, siblings: [...]} and flat structure
        const productData = finalProduct?.product || finalProduct || null;
        const siblingsData = finalProduct?.siblings || [];

        return {
          product: productData,
          siblings: siblingsData,
          id: cached.id,
          productTimestamp: cached.productTimestamp,
          dynamicTimestamp: cached.dynamicTimestamp,
          timestamp: cached.timestamp,
          dynamicData: cached.dynamicData,
          _cacheStatus: {
            product: productValid,
            dynamic: dynamicValid,
            needsRefresh: !productValid || !dynamicValid,
          },
        };
      } catch (error) {
        console.warn('Failed to get product from cache:', error);
        return null;
      }
    },

    /**
     * Stores a product in the cache with appropriate timestamps
     * @param {string} id - The product id to use as key
     * @param {Object} productData - The product data to cache
     * @param {string} updateType - 'full' or 'dynamic'
     * @returns {Promise<boolean>} True if successfully cached
     */
    set: async (id, productData, updateType = 'full') => {
      try {
        const existing = await getItem(STORES.PRODUCTS_CACHE, id);
        const now = Date.now();

        let cacheItem;

        if (updateType === 'full' || !existing) {
          // Full update - store complete product data
          // Store data in flattened format with product and siblings as top-level properties
          cacheItem = {
            id,
            product: productData.product
              ? productData
              : { product: productData, siblings: [] },
            productTimestamp: now,
            dynamicTimestamp: now,
            timestamp: now,
            dynamicData: null,
          };
        } else if (updateType === 'dynamic') {
          const dynamicData = extractDynamicData(productData);

          cacheItem = {
            id: existing.id,
            product: existing.product,
            productTimestamp: existing.productTimestamp,
            dynamicTimestamp: now,
            timestamp: now,
            dynamicData: dynamicData,
          };
        }

        await putItem(STORES.PRODUCTS_CACHE, cacheItem);
        return true;
      } catch (error) {
        console.warn('Failed to save product to cache:', error);
        return false;
      }
    },

    /**
     * Determines what type of cache update is needed
     * @param {Object} cachedItem - The cached item to check
     * @returns {Object} Object indicating what needs updating
     */
    needsUpdate: (cachedItem) => {
      if (!cachedItem?._cacheStatus) return { full: true };

      const status = cachedItem._cacheStatus;
      return {
        full: !status.product,
        dynamicOnly: status.product && !status.dynamic,
        none: status.product && status.dynamic,
      };
    },

    /**
     * Checks if cached data is valid for specific field types
     * @param {Object} cachedItem - The cached item to check
     * @param {string} fieldType - 'product', 'dynamic', or 'both'
     * @returns {boolean} True if valid for the specified field type
     */
    isValid: (cachedItem, fieldType = 'both') => {
      // Backward compatibility - if no _cacheStatus, fall back to old logic
      if (!cachedItem?._cacheStatus) {
        if (!cachedItem || !cachedItem.timestamp) return false;
        return Date.now() - cachedItem.timestamp < CACHE_DURATIONS.DYNAMIC;
      }

      const status = cachedItem._cacheStatus;

      switch (fieldType) {
        case 'product':
          return status.product;
        case 'dynamic':
          return status.dynamic;
        case 'both':
        default:
          return status.product && status.dynamic;
      }
    },

    /**
     * Removes all cached products by clearing the product cache store
     * @returns {Promise<boolean>} True if successfully cleared
     */
    clear: async () => {
      try {
        await clearStore(STORES.PRODUCTS_CACHE);
        return true;
      } catch (error) {
        console.warn('Failed to clear product cache:', error);
        return false;
      }
    },

    /**
     * Removes expired items from the cache based on field-specific expiry
     * @returns {Promise<boolean>} True if successful
     */
    removeExpired: async () => {
      try {
        const allItems = await getAllItems(STORES.PRODUCTS_CACHE);
        const now = Date.now();

        for (const item of allItems) {
          // For backward compatibility, check old timestamp format first
          if (item.timestamp && !item.productTimestamp && !item.dynamicTimestamp) {
            if (now - item.timestamp > CACHE_DURATIONS.DYNAMIC) {
              await deleteItem(STORES.PRODUCTS_CACHE, item.id);
            }
            continue;
          }

          const productExpired = !item.productTimestamp || now - item.productTimestamp > CACHE_DURATIONS.PRODUCT;
          const dynamicExpired = !item.dynamicTimestamp || now - item.dynamicTimestamp > CACHE_DURATIONS.DYNAMIC;

          // Remove item if both product and dynamic data are expired
          if (productExpired && dynamicExpired) {
            await deleteItem(STORES.PRODUCTS_CACHE, item.id);
          }
        }

        return true;
      } catch (error) {
        console.warn('Failed to remove expired items from cache:', error);
        return false;
      }
    },
  };
};

// For backward compatibility, export as productCache
const productCache = smartProductCache;

const debounce = (func, wait) => {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
};

const isLoggedIn = () => {
  return window.Shopify?.customer_logged_in === true;
};

const getCurrentMarketContext = () => {
  return {
    currency: window.Shopify?.currency?.active || null,
    country: window.Shopify?.country,
  };
};

function isSuspicious(input) {
  const blacklist = [
    /<script.*?>/i,
    /on\w+=/i,
    /(\.\.\/)/,
  ];
  return blacklist.some((pattern) => pattern.test(input));
}

export { moveWithinArray, moveAfterItem, moveAfterItemByItem, isEmptyObject, findInObj, asNumber, formatString, isDefined, isElement, handleize, removeParams, convertObjectToFormData, convertFormDataToObject, wait, safeSessionStorage, productCache, debounce, isLoggedIn, getCurrentMarketContext, isSuspicious };
